import { useMemo, useState } from 'react'
import { Betrag } from './Betrag'
import { api, betrag, periodenname } from './api'
import { belegtext, satz, t, anzahlwort } from './sprache'
import { Zustand } from './Zustand'
import { Zuordnungsfenster } from './Zuordnungsfenster'
import type { Importvorschau, Stapelbericht, Stapelvorschau } from './typen'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import { Vorschlagsfrage, alleBeantwortet } from './Bewegungsartvorschlaege'
import type { Bewegungsartvorschlag } from './Datenquellen'
import { useDarf } from './Rechte'

/**
 * Die Prüfliste eines Stapels — und der eine Knopf, der ihn bucht
 * (5.1 Abschnitt 3.22, R729, R730).
 *
 * **Der erste Schritt schreibt nichts.** Was hier steht, ist gelesen und
 * geprüft: je Datei der erkannte Monat, die Zeilen, die Probe und der
 * Datenimport, den sie ersetzen würde. Gebucht wird erst auf Knopfdruck — und
 * erst, wenn keine Zeile mehr einen Befund trägt.
 *
 * **Das Offene ist gesammelt und nicht je Datei.** Ein Konto, das in
 * vierundzwanzig Dateien steht, wird einmal zugeordnet: Das Zuordnungsfenster
 * bekommt die Zeilen aller Dateien in einer Vorschau. Ohne das wäre dieselbe
 * Handbewegung vierundzwanzigmal zu tun — und genau die soll der Stapel sparen.
 */
export function Stapelliste({
  vorschau,
  einheit,
  quelle,
  vorschlaege,
  laeuft,
  aufNeuPruefen,
  aufFertig,
}: {
  vorschau: Stapelvorschau
  einheit: string
  quelle: string | null
  /**
   * Die Vorschläge der Datenquelle (R871) — **alle, nicht nur die getroffenen.**
   * Welcher Monat getroffen ist, steht erst beim Buchen fest (R749); gefragt
   * wird deshalb jeder Vorschlag einmal für den Stapel (R873).
   */
  vorschlaege: Bewegungsartvorschlag[]
  laeuft: boolean
  /** Nach dem Zuordnen wird der ganze Stapel neu gelesen (R592, R729). */
  aufNeuPruefen: () => void
  /** Gebucht — mit den Läufen der Monate, die die Liste danach markiert (7.2 Abschnitt 35, R1136). */
  aufFertig: (laeufe: number[]) => void
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('stapel:stapelliste')

  const darf = useDarf()
  const [fensterOffen, setFensterOffen] = useState(false)
  const [bericht, setBericht] = useState<Stapelbericht | null>(null)
  const [bucht, setBucht] = useState(false)
  /** Die Antwort je Vorschlag, erkannt an Konto und Richtung (R871, R873). */
  const [antworten, setAntworten] = useState<Record<string, boolean>>({})
  const fragen = vorschlaege.map((v) => ({
    ...v,
    zeilen: 0,
    betrag: 0,
    angenommen: antworten[`${v.konto}|${v.richtung}`] ?? null,
  }))
  const beantwortet = alleBeantwortet(fragen)

  const gelesen = useMemo(
    () => vorschau.eintraege.filter((e) => e.vorschau !== null && e.periode !== null),
    [vorschau],
  )

  /*
    **Eine Vorschau über alle Dateien** — nur für das Zuordnungsfenster (R729).
    Sie wird nie gebucht: Gebucht werden die einzelnen Vorschauen, jede in ihre
    Periode. Hier steht sie, damit das Fenster alles Offene auf einmal führt.
  */
  const gebuendelt: Importvorschau | null = useMemo(() => {
    if (gelesen.length === 0) {
      return null
    }

    return {
      ...gelesen[0]!.vorschau!,
      dateiname: t('import.stapel.alle', { anzahl: anzahlwort(gelesen.length) }),
      zeilen: gelesen.flatMap((e) => e.vorschau!.zeilen),
    }
  }, [gelesen])

  /*
    **Zugeordnet wird ab dem ersten gelieferten Monat.** Eine Zuordnung gilt ab
    einer Periode (R1–R3); nähme man die letzte, stünden die dreiundzwanzig
    Monate davor weiter ohne Konto da.
  */
  const ersteperiode = useMemo(
    () => Math.min(...gelesen.map((e) => e.periode!)),
    [gelesen],
  )

  const monate = gelesen.map((e) => e.periode!)

  async function uebernehmen() {
    setBucht(true)
    try {
      const ergebnis = await api.stapelUebernehmen(
        gelesen.map((e) => ({
          vorschau: e.vorschau!,
          ersetzt: e.ersetztBeleg,
          // Ohne Namen nimmt der Rechenkern seinen Vorschlag (R374) — je Monat
          // einen eigenen, und das ist hier der richtige: Vierundzwanzig
          // Datenimporte unter demselben Namen wären nicht auseinanderzuhalten.
          name: null,
        })),
        quelle,
        null,
        fragen.map((f) => ({ konto: f.konto, richtung: f.richtung, angenommen: f.angenommen === true })),
      )

      setBericht(ergebnis)
      // Ohne „12 Monate übernommen: Januar bis Dezember" (7.2 Abschnitt 34,
      // R1131) — die Liste zeigt die Monate, eingegrenzt auf ihr Jahr (R1138).
      if (ergebnis.istVollstaendig) {
        aufFertig(ergebnis.gebucht.map((g) => g.lauf))
      }
    } finally {
      setBucht(false)
    }
  }

  /*
    **Das Hindernis steht allein** (R144): Eine Quelle, die nicht sagt, woher
    die Periode kommt, kann keinen Stapel tragen — und eine Tabelle darunter
    beantwortete keine Frage, die dann noch offen wäre.
  */
  if (vorschau.hindernis) {
    return (
      <div className="abschnitt">
        <h2>{t('import.stapel.titel', { anzahl: anzahlwort(vorschau.dateien) })}</h2>
        <p className="hinweis">{satz(vorschau.hindernis)}</p>
      </div>
    )
  }

  return (
    <div className="abschnitt">
      <h2>{t('import.stapel.titel', { anzahl: anzahlwort(vorschau.dateien) })}</h2>

      <div className={fensterOffen ? 'kennzahlen veraltet' : 'kennzahlen'}>
        <div className="kennzahl">
          <span className="name">{t('import.stapel.monate')}</span>
          <span className="wert">
            {monate.length === 0
              ? '—'
              : `${periodenname(Math.min(...monate))} – ${periodenname(Math.max(...monate))}`}
          </span>
        </div>
        <div className="kennzahl">
          <span className="name">{t('import.stapel.dateien')}</span>
          <span className="wert">{vorschau.dateien}</span>
        </div>
        <div className="kennzahl">
          <span className="name">{t('import.stapel.ersetzt')}</span>
          <span className="wert">{vorschau.ersetzt}</span>
        </div>
        <div className="kennzahl">
          <span className="name">{t('import.vorschau.probe')}</span>
          <span className="wert">
            <Zustand
              art={vorschau.istBuchbar ? 'fertig' : 'offen'}
              text={
                vorschau.istBuchbar
                  ? t('import.stapel.bereit')
                  : t('import.stapel.befunde', {
                      anzahl: anzahlwort(vorschau.eintraege.filter((e) => !e.istBuchbar).length),
                    })
              }
            />
          </span>
        </div>
      </div>

      {fensterOffen && <p className="hinweis">{t('import.zuordnung.veraltet')}</p>}

      {/* Einmal für den Stapel, nicht je Monat (R873). */}
      <Vorschlagsfrage
        vorschlaege={fragen}
        mitUmfang={false}
        laeuft={laeuft || bucht}
        aufAntwort={(i, angenommen) =>
          setAntworten((a) => ({ ...a, [`${fragen[i]!.konto}|${fragen[i]!.richtung}`]: angenommen }))
        }
      />

      <p className="handreichung">
        {darf('zuordnen') && vorschau.offen > 0 && gebuendelt && (
          <button type="button" disabled={laeuft || bucht} onClick={() => setFensterOffen(true)}>
            {t(
              vorschau.offen === 1 ? 'import.zuordnung.oeffnen.eins' : 'import.zuordnung.oeffnen.mehrere',
              { anzahl: anzahlwort(vorschau.offen) },
            )}
          </button>
        )}{' '}
        {/* Wer nicht buchen darf, sieht den Knopf nicht und auch nicht, warum er gesperrt wäre (R1180). */}
        {darf('stapelUebernehmen') && (
          <>
            <button
              type="button"
              className="haupt"
              disabled={laeuft || bucht || !vorschau.istBuchbar || !beantwortet}
              onClick={() => void uebernehmen()}
            >
              {t(bucht ? 'import.stapel.buchtgerade' : 'import.stapel.uebernehmen', {
                anzahl: anzahlwort(gelesen.length),
              })}
            </button>

            {/* Nur der gesperrte Knopf sagt warum (R144). */}
            {!vorschau.istBuchbar && (
              <span className="erlaeuterung"> {t('import.stapel.gesperrt')}</span>
            )}
            {vorschau.istBuchbar && !beantwortet && (
              <span className="erlaeuterung"> {t('import.uebernehmen.vorschlaege')}</span>
            )}
          </>
        )}
      </p>

      {/*
        **Der Bericht steht über der Liste**, sobald gebucht wurde: Die Frage
        danach lautet „wie weit ist er gekommen", und nicht „was stand in
        Datei sieben".
      */}
      {/* Ein ganz gebuchter Stapel geht in die Liste, das Kästchen sagt es (7.2 Abschnitt 34, R1132). */}
      {bericht && !bericht.istVollstaendig && (
        <div className="hinweis">
          <p>
            {t('import.stapel.bericht.halt', {
              gebucht: anzahlwort(bericht.gebucht.length),
              datei: bericht.haltdatei ?? '—',
            })}
          </p>
          {bericht.halt && <p>{satz(bericht.halt)}</p>}
        </div>
      )}

      <div className="tabellenrahmen">
        <table ref={spaltenrahmen} className="ziehbaretabelle">
          <Spaltenmassleiste masse={spaltenmasse} />
          <thead>
            <tr>
              <th>{t('import.stapel.spalte.monat')}</th>
              <th>{t('import.stapel.spalte.datei')}</th>
              <th className="zahl">{t('import.spalte.gelesen')}</th>
              <th className="zahl">{t('import.stapel.spalte.summe')}</th>
              <th>{t('import.vorschau.probe')}</th>
              <th>{t('import.stapel.spalte.stand')}</th>
              <th>{t('import.spalte.befund')}</th>
            </tr>
          </thead>
          <tbody>
            {vorschau.eintraege.map((e) => {
              const gebuchtHier = bericht?.gebucht.find((g) => g.periode === e.periode)
              return (
                <tr key={e.dateiname} className={e.istBuchbar ? undefined : 'geaendert'}>
                  <td>{e.periode === null ? '—' : periodenname(e.periode)}</td>
                  <td className="code">{e.dateiname}</td>
                  <td className="zahl">{e.vorschau ? anzahlwort(e.vorschau.gelesen) : '—'}</td>
                  <td className="zahl">{e.vorschau ? <Betrag wert={e.vorschau.summe} /> : '—'}</td>
                  <td>
                    {e.vorschau && (
                      <Zustand
                        art={e.vorschau.gehtAuf ? 'fertig' : 'blockiert'}
                        text={
                          e.vorschau.gehtAuf
                            ? t('import.vorschau.gehtauf')
                            : t('import.vorschau.differenz', { betrag: betrag(e.vorschau.summe) })
                        }
                      />
                    )}
                  </td>
                  <td>
                    {gebuchtHier
                      ? t('import.stapel.stand.gebucht')
                      : e.ersetztBeleg
                        ? t('import.stapel.stand.ersetzt', { name: belegtext(e.ersetztName ?? '') })
                        : t('import.stapel.stand.neu')}
                  </td>
                  <td>{e.befund ? satz(e.befund) : ''}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {fensterOffen && gebuendelt && (
        <Zuordnungsfenster
          vorschau={gebuendelt}
          einheit={einheit}
          periode={ersteperiode}
          aufSchliessen={(geschrieben) => {
            setFensterOffen(false)
            if (geschrieben) {
              aufNeuPruefen()
            }
          }}
        />
      )}
    </div>
  )
}
